import { Link } from 'react-router-dom';
import './InteriorProjects.css';
import { FaPaintRoller, FaCouch, FaLightbulb, FaRulerCombined, FaPalette, FaHome, FaBuilding, FaTools } from 'react-icons/fa';

export default function InteriorProjects() {
  const services = [
    {
      icon: <FaRulerCombined />,
      title: 'Space Planning',
      description: 'Optimized room layouts that maximize functionality and aesthetic appeal for your space.'
    },
    {
      icon: <FaPalette />,
      title: 'Design Consultation',
      description: 'Expert advice on color schemes, materials, textures, and design styles that suit your taste.'
    },
    {
      icon: <FaLightbulb />,
      title: 'Lighting Design',
      description: 'Strategic lighting solutions to enhance ambiance and functionality of every room.'
    },
    {
      icon: <FaCouch />,
      title: 'Furniture Selection',
      description: 'Curated furniture pieces that complement your space and match your lifestyle needs.'
    },
    {
      icon: <FaPaintRoller />,
      title: 'Material & Finishes',
      description: 'Premium quality materials, paints, and finishes for lasting beauty and durability.'
    },
    {
      icon: <FaHome />,
      title: 'Residential Interiors',
      description: 'Complete home interior solutions from concept to final installation and styling.'
    },
    {
      icon: <FaBuilding />,
      title: 'Commercial Interiors',
      description: 'Professional interior design for offices, retail spaces, and hospitality projects.'
    },
    {
      icon: <FaTools />,
      title: 'Project Management',
      description: 'End-to-end project coordination with timely execution and quality assurance.'
    }
  ];

  const projectTypes = [
    {
      title: 'Living Spaces',
      description: 'Transform your living rooms into elegant, comfortable spaces for family gatherings',
      image: 'https://images.pexels.com/photos/1571460/pexels-photo-1571460.jpeg?auto=compress&cs=tinysrgb&w=800'
    },
    {
      title: 'Bedrooms',
      description: 'Create serene, personalized bedrooms that offer comfort and style',
      image: 'https://images.pexels.com/photos/1454806/pexels-photo-1454806.jpeg?auto=compress&cs=tinysrgb&w=800'
    },
    {
      title: 'Kitchens',
      description: 'Modern, functional kitchen designs with smart storage and beautiful aesthetics',
      image: 'https://images.pexels.com/photos/2724749/pexels-photo-2724749.jpeg?auto=compress&cs=tinysrgb&w=800'
    },
    {
      title: 'Office Spaces',
      description: 'Professional office interiors that boost productivity and create lasting impressions',
      image: 'https://images.pexels.com/photos/1957477/pexels-photo-1957477.jpeg?auto=compress&cs=tinysrgb&w=800'
    },
    {
      title: 'Retail Outlets',
      description: 'Attractive retail interiors designed to enhance customer experience and sales',
      image: 'https://images.pexels.com/photos/135620/pexels-photo-135620.jpeg?auto=compress&cs=tinysrgb&w=800'
    },
    {
      title: 'Hospitality',
      description: 'Luxurious hotel and restaurant interiors that create memorable experiences',
      image: 'https://images.pexels.com/photos/262048/pexels-photo-262048.jpeg?auto=compress&cs=tinysrgb&w=800'
    }
  ];

  const processSteps = [
    {
      number: '01',
      title: 'Initial Consultation',
      description: 'Understanding your vision, requirements, lifestyle, and budget constraints.'
    },
    {
      number: '02',
      title: 'Design Development',
      description: 'Creating detailed design concepts, 3D renderings, and material selections.'
    },
    {
      number: '03',
      title: 'Project Execution',
      description: 'Professional execution with quality materials and skilled craftsmanship.'
    },
    {
      number: '04',
      title: 'Final Installation',
      description: 'Complete setup, styling, and handover of your beautifully designed space.'
    }
  ];

  return (
    <>
      <section className="interior-hero">
        <div className="interior-hero-content">
          <h1>Interior Design Projects</h1>
          <p>Transform your spaces with expert interior design solutions</p>
        </div>
      </section>

      <section className="interior-intro">
        <div className="interior-container">
          <div className="intro-content">
            <h2>Creating Beautiful, Functional Spaces</h2>
            <p>
              We specialize in residential and commercial interior design projects that blend aesthetics with 
              functionality. Our experienced design team works closely with you to create spaces that reflect 
              your personality, meet your needs, and exceed your expectations. From concept to completion, we 
              handle every detail with precision and creativity.
            </p>
          </div>
        </div>
      </section>

      <section className="services-section">
        <div className="interior-container">
          <h2 className="section-title">Our Interior Services</h2>
          <div className="services-grid">
            {services.map((service, index) => (
              <div key={index} className="service-card" style={{ animationDelay: `${index * 0.1}s` }}>
                <div className="service-icon">{service.icon}</div>
                <h3>{service.title}</h3>
                <p>{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="project-types-section">
        <div className="interior-container">
          <h2 className="section-title">Project Portfolio</h2>
          <div className="project-types-grid">
            {projectTypes.map((type, index) => (
              <div key={index} className="project-type-card" style={{ animationDelay: `${index * 0.1}s` }}>
                <div className="project-type-image">
                  <img src={type.image} alt={type.title} />
                  <div className="image-overlay">
                    <h3>{type.title}</h3>
                  </div>
                </div>
                <div className="project-type-content">
                  <p>{type.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="process-section">
        <div className="interior-container">
          <h2 className="section-title">Our Design Process</h2>
          <div className="process-steps">
            {processSteps.map((step, index) => (
              <div key={index} className="process-step" style={{ animationDelay: `${index * 0.15}s` }}>
                <div className="step-number">{step.number}</div>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="why-choose-section">
        <div className="interior-container">
          <div className="why-choose-wrapper">
            <div className="why-choose-content">
              <h2>Why Choose Our Interior Services?</h2>
              <ul className="benefits-list">
                <li>15+ years of interior design expertise</li>
                <li>Customized designs tailored to your needs</li>
                <li>Premium quality materials and finishes</li>
                <li>Transparent pricing with no hidden costs</li>
                <li>On-time project delivery guarantee</li>
                <li>Comprehensive after-sales support</li>
                <li>Experienced team of designers and craftsmen</li>
                <li>Complete project management from start to finish</li>
              </ul>
            </div>
            <div className="why-choose-image">
              <img 
                src="https://images.pexels.com/photos/1648776/pexels-photo-1648776.jpeg?auto=compress&cs=tinysrgb&w=800" 
                alt="Interior Design" 
              />
            </div>
          </div>
        </div>
      </section>

      <section className="interior-cta">
        <div className="interior-container">
          <div className="cta-content">
            <h2>Ready to Transform Your Space?</h2>
            <p>Schedule a free consultation with our interior design experts</p>
            <Link to="/contact" className="cta-button">
              Get Free Consultation
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
